import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  CurrencyEuroIcon,
  PlusIcon,
  TrashIcon,
  CalendarIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  TagIcon,
  DocumentTextIcon,
  XMarkIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  UserGroupIcon,
  BoltIcon,
  BeakerIcon,
  Cog6ToothIcon,
  WrenchScrewdriverIcon,
  ShieldCheckIcon,
  EllipsisHorizontalIcon,
  BuildingLibraryIcon,
  HeartIcon,
  UserPlusIcon,
  HomeIcon,
  ArrowsRightLeftIcon,
  TruckIcon,
  BookOpenIcon,
  ChevronDownIcon,
  CalculatorIcon,
  PencilSquareIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

import { FaDog } from 'react-icons/fa';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import ConfirmationModal from '../components/ConfirmationModal';
import { ModalShell, ModalActions } from '../components/modals/core';
import StatementDetailModal from '../components/modals/StatementDetailModal';
import AttributeLabel from '../components/AttributeLabel';
import { StatCard, StatGroup } from '../components/StatCard';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import useFinanceDesignContext from '../hooks/useFinanceDesignContext';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

// Create a typed wrapper for FaDog
const DogIcon: React.FC<{ className?: string }> = ({ className }) => {
  const IconComponent = FaDog as React.ComponentType<{ className?: string }>;
  return <IconComponent className={className} />;
};

// Font Awesome to Heroicons mapping
const iconMap: { [key: string]: React.ComponentType<any> } = {
  FaUsers: UserGroupIcon,
  FaBolt: BoltIcon,
  FaTint: BeakerIcon,
  FaUsersCog: Cog6ToothIcon,
  FaTools: WrenchScrewdriverIcon,
  FaShieldAlt: ShieldCheckIcon,
  FaEllipsisH: EllipsisHorizontalIcon,
  FaEuroSign: CurrencyEuroIcon,
  FaBuilding: BuildingLibraryIcon,
  FaTrash: TrashIcon,
  FaHeart: HeartIcon,
  FaUserPlus: UserPlusIcon,
  FaUtensils: HomeIcon,
  FaExchangeAlt: ArrowsRightLeftIcon,
  FaDog: DogIcon,
  FaCar: TruckIcon,
  FaBookOpen: BookOpenIcon,
  // Fallback
  default: TagIcon
};

interface Category {
  id: number;
  name: string;
  icon: string;
}

interface AccountCategory {
  category: string;
  income: number;
  expense: number;
  transactionCount: number;
  icon: string;
}

interface BudgetData {
  [categoryName: string]: {
    income: string | number;
    expense: string | number;
  };
}

interface BudgetPlan {
  id: number;
  name: string;
  year: number;
  importYear: number;
  budgetData: BudgetData;
  createdAt?: string;
  updatedAt?: string;
}

interface ConfirmModalState {
  isOpen: boolean;
  type: 'success' | 'warning' | 'error' | 'question' | 'info';
  title: string;
  message: string;
  variant: 'single' | 'two';
  onConfirm?: () => void;
}

// Eine Kategorie-Zeile innerhalb einer Planung (Soll aus budgetData, Ist aus accountStatements)
interface PlanCategoryRow {
  name: string;
  icon: string;
  sollIncome: number;
  sollExpense: number;
  istIncome: number;
  istExpense: number;
  bookings: any[];
}

// Aggregierte Plan-vs-Ist-Kennzahlen einer einzelnen Budget-Planung
interface PlanMetrics {
  sollIncome: number;
  sollExpense: number;
  sollNet: number;
  istIncome: number;
  istExpense: number;
  istNet: number;
  /** Anteil des Jahres, der bereits vergangen ist (0-100, 100 = Jahr abgeschlossen) */
  progressPct: number;
  rows: PlanCategoryRow[];
}

interface PlanStatusResult {
  level: 'green' | 'orange' | 'red';
  label: string;
  diff?: number;
  worst?: { row: PlanCategoryRow; ratio: number };
}

// Balken-Prozentsatz (gekappt bei 100%) für Fortschrittsbalken
const barPct = (ist: number, soll: number): number => {
  if (soll > 0) return Math.min((ist / soll) * 100, 100);
  return ist > 0 ? 100 : 0;
};

const BudgetPlanning: React.FC = () => {
  useFinanceDesignContext();
  const { user } = useContext(AuthContext);
  const [budgetPlans, setBudgetPlans] = useState<BudgetPlan[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [accountCategories, setAccountCategories] = useState<AccountCategory[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [loading, setLoading] = useState<boolean>(false);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [markedPlanId, setMarkedPlanId] = useState<number | null>(null);
  const [planFilterYear, setPlanFilterYear] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'plan' | 'aggregate'>('plan');
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    variant: 'single'
  });

  // Category Input Modal State
  const [categoryInputModal, setCategoryInputModal] = useState<{
    isOpen: boolean;
    title: string;
    onConfirm: (categoryName: string) => void;
  }>({
    isOpen: false,
    title: '',
    onConfirm: () => {}
  });

  const [categoryInputValue, setCategoryInputValue] = useState('');

  // Neue Budget-Planung Formular
  const [newPlanForm, setNewPlanForm] = useState({
    name: '',
    year: new Date().getFullYear() + 1,
    importYear: new Date().getFullYear(),
  });

  // Multi-Step Modal für Budget-Planung
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [manualBudgetData, setManualBudgetData] = useState<BudgetData>({});
  const [showManualInput, setShowManualInput] = useState<boolean>(false);
  const [stepData, setStepData] = useState({
    step1: {
      name: '',
      year: new Date().getFullYear() + 1,
      importYear: new Date().getFullYear(),
      useImport: true
    },
    step2: {
      budgetData: {} as BudgetData
    }
  });

  // Kontoverlauf
  const [accountStatements, setAccountStatements] = useState<any[]>([]);
  const [selectedStatementId, setSelectedStatementId] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [chartAccordionOpen, setChartAccordionOpen] = useState<boolean>(false);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  // Optimierte Chart-Daten vom Backend
  const [optimizedChartData, setOptimizedChartData] = useState<any[]>([]);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [chartDataLoading, setChartDataLoading] = useState<boolean>(false);

  // Bearbeiten-Modus für die aktive Planung
  const [isEditingPlan, setIsEditingPlan] = useState<boolean>(false);
  const [draftName, setDraftName] = useState<string>('');
  const [draftYear, setDraftYear] = useState<number>(new Date().getFullYear());
  const [draftBudget, setDraftBudget] = useState<BudgetData>({});
  const [isDirty, setIsDirty] = useState<boolean>(false);

  // Berechtigungsprüfung - nur Vorstand darf zugreifen
  const isAuthorized = user && (user.vorstand === 1 || user.admin === 1);

  useEffect(() => {
    if (!isAuthorized) return;

    loadBudgetPlans();
    loadCategories();
    loadAccountCategories(selectedYear);
    loadAccounts();
    loadAccountStatements();
  }, [isAuthorized, selectedYear]);

  useEffect(() => {
    if (chartAccordionOpen && accounts.length > 0) {
      // Lade optimierte Chart-Daten statt aller Statements
      loadOptimizedChartData(planFilterYear || undefined);
    }
  }, [chartAccordionOpen, accounts, planFilterYear]);

  // Standard-Tab: erste/aktuellste Planung markieren (nie null), auch nach Löschen einer Planung neu bestimmen
  useEffect(() => {
    if (budgetPlans.length === 0) {
      if (markedPlanId !== null) setMarkedPlanId(null);
      return;
    }
    const stillExists = budgetPlans.some(p => p.id === markedPlanId);
    if (!stillExists) {
      const sorted = [...budgetPlans].sort((a, b) => (b.year - a.year) || (b.id - a.id));
      setMarkedPlanId(sorted[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budgetPlans]);

  // Chart-/Kontoverlauf-Filterjahr synchron zur aktiven Planung halten
  useEffect(() => {
    if (viewMode === 'aggregate') {
      setPlanFilterYear(null);
      return;
    }
    const active = budgetPlans.find(p => p.id === markedPlanId);
    setPlanFilterYear(active ? active.year : null);
  }, [markedPlanId, viewMode, budgetPlans]);

  const loadBudgetPlans = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/finance/budget-plans');
      setBudgetPlans(response.data);
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Budget-Planungen konnten nicht geladen werden.',
        variant: 'single'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await apiClient.get('/api/finance/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Kategorien konnten nicht geladen werden:', error);
    }
  };

  const loadAccountCategories = async (year: number) => {
    try {
      const response = await apiClient.get(`/api/finance/account-categories?year=${year}`);
      if (response.data.success) {
        setAccountCategories(response.data.categories);
      }
    } catch (error) {
      console.error('Konto-Kategorien konnten nicht geladen werden:', error);
    }
  };

  const loadAccounts = async () => {
    try {
      const response = await apiClient.get('/api/finance/accounts');
      setAccounts(response.data);
    } catch (error) {
      console.error('Konten konnten nicht geladen werden:', error);
    }
  };

  const loadAccountStatements = async () => {
    try {
      // Lade alle Statements in einem einzigen API-Aufruf (statt sequentiell pro Konto)
      const response = await apiClient.get('/api/finance/statements');
      const statements = response.data.map((stmt: any) => ({
        ...stmt,
        account_name: stmt.konto_name || accounts.find(a => a.id === stmt.konto_id)?.name || 'Unbekannt',
        account_id: stmt.konto_id
      }));

      // Sortiere nach Buchungstag
      statements.sort((a: any, b: any) => new Date(a.buchungstag).getTime() - new Date(b.buchungstag).getTime());
      setAccountStatements(statements);
    } catch (error) {
      console.error('Kontoauszüge konnten nicht geladen werden:', error);
    }
  };

  // Optimierte Chart-Daten vom Backend laden (vorberechnet)
  const loadOptimizedChartData = async (year?: number) => {
    setChartDataLoading(true);
    try {
      const url = year
        ? `/api/finance/chart-data?year=${year}`
        : '/api/finance/chart-data';
      const response = await apiClient.get(url);

      if (response.data.success) {
        setOptimizedChartData(response.data.chartData || []);
        setAvailableCategories(response.data.kategorien || []);
      }
    } catch (error) {
      console.error('Optimierte Chart-Daten konnten nicht geladen werden:', error);
      // Fallback: Lade alte Statements
      loadAccountStatements();
    } finally {
      setChartDataLoading(false);
    }
  };

  // Schritt-Navigation
  const nextStep = async () => {
    if (currentStep === 1) {
      // Validierung für Schritt 1
      if (!stepData.step1.name.trim()) {
        setConfirmModal({
          isOpen: true,
          type: 'error',
          title: 'Fehler',
          message: 'Bitte geben Sie einen Namen für die Budget-Planung ein.',
          variant: 'single'
        });
        return;
      }

      // Lade Budget-Daten für Schritt 2
      if (stepData.step1.useImport) {
        try {
          const response = await apiClient.get(`/api/finance/account-categories?year=${stepData.step1.importYear}`);
          const budgetData: BudgetData = {};

          if (response.data.success) {
            response.data.categories.forEach((cat: AccountCategory) => {
              budgetData[cat.category] = {
                income: cat.income,
                expense: cat.expense
              };
            });
          }

          setStepData(prev => ({
            ...prev,
            step2: { budgetData }
          }));
        } catch (error) {
          console.error('Fehler beim Laden der Import-Daten:', error);
        }
      } else {
        // Initiale leere Struktur für manuelle Eingabe - zeige alle verfügbaren Kategorien
        try {
          const response = await apiClient.get('/api/finance/categories');
          const initialData: BudgetData = {};

          response.data.forEach((cat: Category) => {
            initialData[cat.name] = {
              income: '',
              expense: ''
            };
          });

          setStepData(prev => ({
            ...prev,
            step2: { budgetData: initialData }
          }));
        } catch (error) {
          console.error('Fehler beim Laden der Kategorien:', error);
        }
      }

      // Zusätzlich: Alle verfügbaren Kategorien laden und mit bestehenden Budget-Daten mergen
      try {
        const allCategoriesResponse = await apiClient.get('/api/finance/categories');
        const allCategories = allCategoriesResponse.data;

        // Merge mit bestehenden Budget-Daten
        setStepData(prev => {
          const currentBudgetData = { ...prev.step2.budgetData };

          // Füge alle verfügbaren Kategorien hinzu, die noch nicht vorhanden sind
          allCategories.forEach((cat: Category) => {
            if (!currentBudgetData[cat.name]) {
              currentBudgetData[cat.name] = {
                income: '',
                expense: ''
              };
            }
          });

          return {
            ...prev,
            step2: { budgetData: currentBudgetData }
          };
        });
      } catch (error) {
        console.error('Fehler beim Laden aller Kategorien:', error);
      }

      setCurrentStep(2);
    } else if (currentStep === 2) {
      setCurrentStep(3);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const resetModal = () => {
    setCurrentStep(1);
    setStepData({
      step1: {
        name: '',
        year: new Date().getFullYear() + 1,
        importYear: new Date().getFullYear(),
        useImport: true
      },
      step2: {
        budgetData: {} as BudgetData
      }
    });
    setShowCreateModal(false);
  };

  // Manuelle Budget-Daten für Schritt 2 ändern
  const handleStep2BudgetChange = (categoryName: string, field: 'income' | 'expense', value: string) => {
    setStepData(prev => ({
      ...prev,
      step2: {
        budgetData: {
          ...prev.step2.budgetData,
          [categoryName]: {
            ...prev.step2.budgetData[categoryName],
            [field]: value
          }
        }
      }
    }));
  };

  // Neue Kategorie in Schritt 2 hinzufügen
  const addStep2Category = () => {
    setCategoryInputModal({
      isOpen: true,
      title: 'Neue Kategorie hinzufügen',
      onConfirm: (categoryName: string) => {
        if (categoryName && categoryName.trim()) {
          setStepData(prev => ({
            ...prev,
            step2: {
              budgetData: {
                ...prev.step2.budgetData,
                [categoryName.trim()]: {
                  income: '',
                  expense: ''
                }
              }
            }
          }));
        }
        setCategoryInputModal({ isOpen: false, title: '', onConfirm: () => {} });
        setCategoryInputValue('');
      }
    });
  };

  // Kategorie aus Schritt 2 entfernen
  const removeStep2Category = (categoryName: string) => {
    setStepData(prev => {
      const newBudgetData = { ...prev.step2.budgetData };
      delete newBudgetData[categoryName];
      return {
        ...prev,
        step2: {
          budgetData: newBudgetData
        }
      };
    });
  };

  const handleCreateBudgetPlan = async () => {
    setLoading(true);
    try {
      const payload = {
        name: stepData.step1.name,
        year: stepData.step1.year,
        importYear: stepData.step1.importYear,
        budgetData: stepData.step2.budgetData
      };

      await apiClient.post('/api/finance/budget-plans', payload);

      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Erfolgreich',
        message: 'Budget-Planung wurde erfolgreich erstellt.',
        variant: 'single'
      });

      resetModal();
      loadBudgetPlans();
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Budget-Planung konnte nicht erstellt werden.',
        variant: 'single'
      });
    } finally {
      setLoading(false);
    }
  };

  // Initialisiere manuelle Eingaben mit vorhandenen Kategorien
  const initializeManualBudgetData = async () => {
    try {
      const response = await apiClient.get('/api/finance/categories');
      const initialData: BudgetData = {};

      response.data.forEach((cat: Category) => {
        initialData[cat.name] = {
          income: '',
          expense: ''
        };
      });

      setManualBudgetData(initialData);
    } catch (error) {
      console.error('Kategorien für manuelle Eingabe konnten nicht geladen werden:', error);
    }
  };

  // Manuelle Budget-Daten ändern
  const handleManualBudgetChange = (categoryName: string, field: 'income' | 'expense', value: string) => {
    setManualBudgetData(prev => ({
      ...prev,
      [categoryName]: {
        ...prev[categoryName],
        [field]: value
      }
    }));
  };

  // Neue Kategorie für manuelle Eingabe hinzufügen
  const addManualCategory = () => {
    setCategoryInputModal({
      isOpen: true,
      title: 'Neue Kategorie hinzufügen',
      onConfirm: (categoryName: string) => {
        if (categoryName && categoryName.trim()) {
          setManualBudgetData(prev => ({
            ...prev,
            [categoryName.trim()]: {
              income: '',
              expense: ''
            }
          }));
        }
        setCategoryInputModal({ isOpen: false, title: '', onConfirm: () => {} });
        setCategoryInputValue('');
      }
    });
  };

  // Kategorie aus manueller Eingabe entfernen
  const removeManualCategory = (categoryName: string) => {
    setManualBudgetData(prev => {
      const newData = { ...prev };
      delete newData[categoryName];
      return newData;
    });
  };

  const handleDeleteBudgetPlan = (plan: BudgetPlan) => {
    setConfirmModal({
      isOpen: true,
      type: 'warning',
      title: 'Löschen bestätigen',
      message: `Möchten Sie die Budget-Planung "${plan.name}" wirklich löschen?`,
      variant: 'two',
      onConfirm: async () => {
        try {
          await apiClient.delete(`/api/finance/budget-plans/${plan.id}`);
          setConfirmModal({
            isOpen: true,
            type: 'success',
            title: 'Erfolgreich',
            message: 'Budget-Planung wurde gelöscht.',
            variant: 'single'
          });
          loadBudgetPlans();
        } catch (error) {
          setConfirmModal({
            isOpen: true,
            type: 'error',
            title: 'Fehler',
            message: 'Budget-Planung konnte nicht gelöscht werden.',
            variant: 'single'
          });
        }
      }
    });
  };

  // PUT /api/finance/budget-plans/:id – Planung aktualisieren (Name, Jahr, Soll-Budgetdaten)
  const handleUpdateBudgetPlan = async (plan: BudgetPlan, name: string, year: number, budgetData: BudgetData) => {
    setLoading(true);
    try {
      await apiClient.put(`/api/finance/budget-plans/${plan.id}`, { name, year, budgetData });
      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Erfolgreich',
        message: 'Budget-Planung wurde aktualisiert.',
        variant: 'single'
      });
      setIsEditingPlan(false);
      setIsDirty(false);
      setDraftBudget({});
      await loadBudgetPlans();
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Budget-Planung konnte nicht aktualisiert werden.',
        variant: 'single'
      });
    } finally {
      setLoading(false);
    }
  };

  // Aggregierte Soll-Kennzahlen über ALLE Planungen (nur für die Gesamt-Ansicht)
  const aggregateStatistics = useMemo(() => {
    let totalIncome = 0;
    let totalExpense = 0;

    budgetPlans.forEach(plan => {
      Object.values(plan.budgetData).forEach(data => {
        const income = typeof data.income === 'string' ? parseFloat(data.income) || 0 : data.income || 0;
        const expense = typeof data.expense === 'string' ? parseFloat(data.expense) || 0 : data.expense || 0;
        totalIncome += income;
        totalExpense += expense;
      });
    });

    return { totalPlans: budgetPlans.length, totalIncome, totalExpense, netBudget: totalIncome - totalExpense };
  }, [budgetPlans]);

  // Chart-Daten für zeitlichen Verlauf - Budget-Planungen (Soll je Jahr)
  const budgetChartData = useMemo(() => {
    if (!budgetPlans || budgetPlans.length === 0) {
      return [];
    }

    const yearData = new Map<number, { income: number; expense: number }>();

    budgetPlans.forEach(plan => {
      if (!yearData.has(plan.year)) {
        yearData.set(plan.year, { income: 0, expense: 0 });
      }

      const yearTotals = yearData.get(plan.year)!;

      if (plan.budgetData && typeof plan.budgetData === 'object') {
        Object.values(plan.budgetData).forEach(data => {
          if (data && typeof data === 'object') {
            const income = typeof data.income === 'string' ? parseFloat(data.income) || 0 : (typeof data.income === 'number' ? data.income : 0);
            const expense = typeof data.expense === 'string' ? parseFloat(data.expense) || 0 : (typeof data.expense === 'number' ? data.expense : 0);

            yearTotals.income += income;
            yearTotals.expense += expense;
          }
        });
      }
    });

    return Array.from(yearData.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([year, totals]) => ({
        year: year.toString(),
        'Geplante Einnahmen': totals.income,
        'Geplante Ausgaben': totals.expense,
        'Netto': totals.income - totals.expense
      }));
  }, [budgetPlans]);

  // Chart-Daten für Kontoverlauf (gefiltert nach Jahr wenn Planung markiert)
  const accountChartData = useMemo(() => {
    if (!accountStatements.length || !accounts.length) return [];

    // Berechne Startbetrag aller Konten
    const totalStartbetrag = accounts.reduce((sum, account) => sum + (account.startbetrag || 0), 0);

    // Filtere nur echte Buchungen (keine Umbuchungen zwischen Konten)
    let filteredStatements = accountStatements.filter(stmt =>
      stmt.konto_id && !stmt.von_konto_id && !stmt.nach_konto_id
    );

    let baseOffset = 0;

    if (planFilterYear) {
      const previousYearStatements = filteredStatements.filter(stmt => {
        const year = new Date(stmt.buchungstag).getFullYear();
        return year < planFilterYear;
      });

      baseOffset = totalStartbetrag + previousYearStatements.reduce((sum, stmt) => sum + parseFloat(stmt.betrag), 0);

      filteredStatements = filteredStatements.filter(stmt => {
        const year = new Date(stmt.buchungstag).getFullYear();
        return year === planFilterYear;
      });
    }

    const sortedStatements = [...filteredStatements].sort((a, b) =>
      new Date(a.buchungstag).getTime() - new Date(b.buchungstag).getTime()
    );

    const monthlyData = new Map<string, { date: string, totalBetrag: number }>();
    let runningTotal = planFilterYear ? 0 : totalStartbetrag;

    sortedStatements.forEach(stmt => {
      const date = new Date(stmt.buchungstag);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      runningTotal += parseFloat(stmt.betrag);

      monthlyData.set(monthKey, {
        date: monthKey,
        totalBetrag: runningTotal
      });
    });

    return Array.from(monthlyData.values())
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(data => ({
        monat: data.date,
        'Kontosaldo': data.totalBetrag
      }));
  }, [accountStatements, planFilterYear, accounts]);

  // Kombinierte Chart-Daten - nutze optimierte Backend-Daten wenn verfügbar
  const combinedChartData = useMemo(() => {
    if (optimizedChartData.length > 0) {
      return optimizedChartData;
    }
    return accountChartData;
  }, [optimizedChartData, accountChartData]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  // ---------------------------------------------------------------------
  // Plan-Kennzahlen (Soll aus budgetData, Ist aus accountStatements) + Status-Logik
  // ---------------------------------------------------------------------

  const computeYearProgress = (year: number): number => {
    const now = new Date();
    const currentYear = now.getFullYear();
    if (year < currentYear) return 100;
    if (year > currentYear) return 0;
    const start = new Date(year, 0, 1).getTime();
    const end = new Date(year + 1, 0, 1).getTime();
    return Math.min(100, Math.max(0, ((now.getTime() - start) / (end - start)) * 100));
  };

  const getPlanMetrics = (plan: BudgetPlan): PlanMetrics => {
    const toNum = (v: string | number) => typeof v === 'string' ? parseFloat(v) || 0 : (v || 0);

    const sollIncome = Object.values(plan.budgetData).reduce((s, d) => s + toNum(d.income), 0);
    const sollExpense = Object.values(plan.budgetData).reduce((s, d) => s + toNum(d.expense), 0);

    // Ist-Buchungen des Planungsjahres (nur echte Buchungen, keine Umbuchungen)
    const yearStatements = accountStatements.filter(stmt => (
      new Date(stmt.buchungstag).getFullYear() === plan.year &&
      stmt.konto_id && !stmt.von_konto_id && !stmt.nach_konto_id
    ));
    const sumCats = (stmt: any) => (stmt.categories || []).reduce((a: number, c: any) => a + Number(c.betrag), 0);
    const istIncome = yearStatements.reduce((s, stmt) => stmt.betrag > 0 ? s + sumCats(stmt) : s, 0);
    const istExpense = yearStatements.reduce((s, stmt) => stmt.betrag < 0 ? s + sumCats(stmt) : s, 0);

    // Kategorien aggregieren (Plan + Ist)
    const catMap = new Map<string, PlanCategoryRow>();
    Object.entries(plan.budgetData).forEach(([name, d]) => {
      const categoryDef = categories.find(c => c.name === name);
      catMap.set(name, {
        name,
        icon: categoryDef?.icon || 'default',
        sollIncome: toNum(d.income),
        sollExpense: toNum(d.expense),
        istIncome: 0,
        istExpense: 0,
        bookings: []
      });
    });
    yearStatements.forEach(stmt => {
      (stmt.categories || []).forEach((cat: any) => {
        const c = categories.find(x => x.id === cat.kategorie_id);
        if (!c) return;
        let entry = catMap.get(c.name);
        if (!entry) {
          entry = { name: c.name, icon: c.icon, sollIncome: 0, sollExpense: 0, istIncome: 0, istExpense: 0, bookings: [] };
        }
        if (stmt.betrag > 0) entry.istIncome += Number(cat.betrag);
        else entry.istExpense += Number(cat.betrag);
        entry.bookings.push(stmt);
        catMap.set(c.name, entry);
      });
    });

    // Zeilen: nur Kategorien mit Werten, Einnahmen zuerst, dann nach Volumen sortiert
    const rows = Array.from(catMap.values())
      .filter(r => r.sollIncome > 0 || r.sollExpense > 0 || r.istIncome > 0 || r.istExpense > 0)
      .sort((a, b) => {
        const aInc = a.sollIncome > 0 || a.istIncome > 0;
        const bInc = b.sollIncome > 0 || b.istIncome > 0;
        if (aInc && !bInc) return -1;
        if (!aInc && bInc) return 1;
        if (aInc && bInc) return (b.sollIncome + b.istIncome) - (a.sollIncome + a.istIncome);
        return (b.sollExpense + b.istExpense) - (a.sollExpense + a.istExpense);
      });

    return {
      sollIncome,
      sollExpense,
      sollNet: sollIncome - sollExpense,
      istIncome,
      istExpense,
      istNet: istIncome - istExpense,
      progressPct: computeYearProgress(plan.year),
      rows
    };
  };

  // "Kritisch": Ausgaben-Kategorie hat bereits > 110% ihres vollen Jahres-Solls verbraucht (nur bei laufendem Jahr relevant)
  const getWorstCategory = (metrics: PlanMetrics): { row: PlanCategoryRow; ratio: number } | null => {
    let worst: PlanCategoryRow | null = null;
    let worstRatio = 0;
    metrics.rows.forEach(row => {
      if (row.sollExpense > 0) {
        const ratio = (row.istExpense / row.sollExpense) * 100;
        if (ratio > 110 && ratio > worstRatio) {
          worst = row;
          worstRatio = ratio;
        }
      }
    });
    return worst ? { row: worst, ratio: worstRatio } : null;
  };

  const getPlanStatus = (metrics: PlanMetrics): PlanStatusResult => {
    // Kategorie-Eskalation zu "Kritisch" nur bei laufendem Jahr. Bei abgeschlossenem Jahr ist ein
    // Kategorie-Überzug nur noch historische Abweichung (orange), kein "Kritisch" mehr.
    const worst = metrics.progressPct < 99 ? getWorstCategory(metrics) : null;
    if (worst) {
      return { level: 'red', label: 'Kritisch', worst };
    }

    if (metrics.progressPct >= 99) {
      const closeDiff = metrics.istNet - metrics.sollNet;
      const threshold = Math.abs(metrics.sollNet) * 0.03;
      if (Math.abs(closeDiff) <= threshold) {
        return { level: 'green', label: 'Im Plan' };
      }
      return { level: 'orange', label: 'Abweichung', diff: closeDiff };
    }

    const expectedNet = metrics.sollNet * (metrics.progressPct / 100);
    const diff = metrics.istNet - expectedNet;
    const rel = expectedNet !== 0 ? Math.abs(diff / expectedNet) : (metrics.istNet === 0 ? 0 : 1);
    if (rel <= 0.12) {
      return { level: 'green', label: 'Im Plan' };
    }
    return { level: 'orange', label: 'Abweichung', diff };
  };

  const renderStatusMessage = (metrics: PlanMetrics, status: PlanStatusResult): React.ReactNode => {
    if (status.level === 'red' && status.worst) {
      const { row, ratio } = status.worst;
      return (
        <>
          <span className="font-semibold">{row.name}</span> hat bereits {formatCurrency(row.istExpense)} verbraucht – das sind{' '}
          {ratio.toLocaleString('de-DE', { maximumFractionDigits: 0 })}% des kompletten Jahresbudgets von {formatCurrency(row.sollExpense)}, obwohl erst{' '}
          {Math.round(metrics.progressPct)}% des Jahres vergangen sind. Sofortige Prüfung empfohlen.
        </>
      );
    }
    if (status.level === 'orange') {
      const diff = status.diff || 0;
      if (metrics.progressPct >= 99) {
        return `Das Jahresergebnis liegt ${formatCurrency(Math.abs(diff))} ${diff < 0 ? 'unter' : 'über'} der Planung. Für die nächste Planung ggf. betroffene Kategorien anpassen.`;
      }
      return `Das Ergebnis weicht mit ${formatCurrency(Math.abs(diff))} vom erwarteten Zwischenstand ab.`;
    }
    return metrics.progressPct >= 99
      ? 'Das Jahresergebnis liegt nah an der Planung.'
      : 'Einnahmen und Ausgaben liegen im Rahmen des für diesen Zeitpunkt erwarteten Verlaufs.';
  };

  // ---------------------------------------------------------------------
  // Bearbeiten-Modus (Tab-Wechsel / Gesamt-Toggle / Löschen fragen bei ungespeicherten Änderungen)
  // ---------------------------------------------------------------------

  const discardEdit = () => {
    setIsEditingPlan(false);
    setIsDirty(false);
    setDraftBudget({});
  };

  const guardUnsavedChanges = (action: () => void) => {
    if (isEditingPlan && isDirty) {
      setConfirmModal({
        isOpen: true,
        type: 'warning',
        title: 'Änderungen verwerfen?',
        message: 'Du hast ungespeicherte Änderungen an dieser Planung. Wenn du fortfährst, gehen sie verloren.',
        variant: 'two',
        onConfirm: () => {
          discardEdit();
          action();
        }
      });
    } else {
      action();
    }
  };

  const switchPlan = (id: number) => {
    guardUnsavedChanges(() => {
      setViewMode('plan');
      setMarkedPlanId(id);
    });
  };

  const toggleAggregate = () => {
    guardUnsavedChanges(() => {
      setViewMode(prev => (prev === 'aggregate' ? 'plan' : 'aggregate'));
    });
  };

  const enterEditMode = (plan: BudgetPlan) => {
    setDraftName(plan.name);
    setDraftYear(plan.year);
    setDraftBudget(JSON.parse(JSON.stringify(plan.budgetData)));
    setIsDirty(false);
    setIsEditingPlan(true);
    setExpandedCategory(null);
  };

  const handleCancelEdit = () => {
    if (isDirty) {
      setConfirmModal({
        isOpen: true,
        type: 'warning',
        title: 'Änderungen verwerfen?',
        message: 'Du hast ungespeicherte Änderungen an dieser Planung. Wenn du fortfährst, gehen sie verloren.',
        variant: 'two',
        onConfirm: () => discardEdit()
      });
    } else {
      discardEdit();
    }
  };

  const handleDraftCategoryChange = (categoryName: string, field: 'income' | 'expense', value: string) => {
    setDraftBudget(prev => ({
      ...prev,
      [categoryName]: {
        ...prev[categoryName],
        [field]: value
      }
    }));
    setIsDirty(true);
  };

  const handleRemoveDraftCategory = (categoryName: string) => {
    setDraftBudget(prev => {
      const next = { ...prev };
      delete next[categoryName];
      return next;
    });
    setIsDirty(true);
  };

  const handleAddDraftCategory = () => {
    setCategoryInputModal({
      isOpen: true,
      title: 'Neue Kategorie hinzufügen',
      onConfirm: (categoryName: string) => {
        if (categoryName && categoryName.trim()) {
          setDraftBudget(prev => ({
            ...prev,
            [categoryName.trim()]: { income: '', expense: '' }
          }));
          setIsDirty(true);
        }
        setCategoryInputModal({ isOpen: false, title: '', onConfirm: () => {} });
        setCategoryInputValue('');
      }
    });
  };

  const handleSaveEdit = (plan: BudgetPlan) => {
    const errors: string[] = [];
    if (!draftName.trim()) errors.push('Bitte einen Namen für die Planung angeben.');

    const cleanedBudget: BudgetData = {};
    Object.entries(draftBudget).forEach(([name, data]) => {
      if (!name.trim()) {
        errors.push('Jede Kategorie braucht einen Namen.');
        return;
      }
      const income = typeof data.income === 'string' ? parseFloat(data.income) || 0 : (data.income || 0);
      const expense = typeof data.expense === 'string' ? parseFloat(data.expense) || 0 : (data.expense || 0);
      if (income < 0 || expense < 0) {
        errors.push('Beträge dürfen nicht negativ sein.');
      }
      cleanedBudget[name] = { income, expense };
    });

    if (errors.length > 0) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: errors[0],
        variant: 'single'
      });
      return;
    }

    handleUpdateBudgetPlan(plan, draftName.trim(), draftYear, cleanedBudget);
  };

  if (!isAuthorized) {
    return (
      <div className="finance-page max-w-7xl mx-auto">
        <div className="bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-xl p-6">
          <div className="flex items-center gap-3">
            <ExclamationTriangleIcon className="h-8 w-8 text-error-600" />
            <div>
              <h1 className="text-lg font-semibold text-error-900 dark:text-error-100">
                Zugriff verweigert
              </h1>
              <p className="text-error-700 dark:text-error-300">
                Diese Seite ist nur für Vorstandsmitglieder zugänglich.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Aktive Planung + ihre Kennzahlen/Status für die Render-Phase
  const activePlan = budgetPlans.find(p => p.id === markedPlanId) || null;
  const activeMetrics = activePlan ? getPlanMetrics(activePlan) : null;
  const activeStatus = activeMetrics ? getPlanStatus(activeMetrics) : null;
  const showIncomeTop = !!activeMetrics && (activeMetrics.sollIncome > 0 || activeMetrics.istIncome > 0);
  const showExpenseTop = !!activeMetrics && (activeMetrics.sollExpense > 0 || activeMetrics.istExpense > 0);
  const draftSollNet = Object.values(draftBudget).reduce((sum, d) => {
    const inc = typeof d.income === 'string' ? parseFloat(d.income) || 0 : (d.income || 0);
    const exp = typeof d.expense === 'string' ? parseFloat(d.expense) || 0 : (d.expense || 0);
    return sum + inc - exp;
  }, 0);
  const sortedTabs = [...budgetPlans].sort((a, b) => (b.year - a.year) || (b.id - a.id));

  return (
    <div className="finance-page max-w-7xl mx-auto pb-20 sm:pb-0">
      {/* Header - Variante 2: Titel + Button (Desktop) */}
      <header className="app-page-header finance-page-header hidden sm:block mb-6">
        <div className="p-4 md:py-6 md:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3.5">
            <span className="finance-header-icon"><CalculatorIcon /></span>
            <div>
              <p className="finance-kicker">Finanzen · Vorausschau</p>
              <h1 className="finance-title">Budget-Planung</h1>
              <p className="finance-subtitle">Soll, Ist und Jahresentwicklung verlässlich steuern</p>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center justify-center gap-2 w-10 h-10 md:w-auto md:h-auto md:px-5 md:py-3 rounded-xl bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white text-sm font-semibold transition-colors"
            >
              <PlusIcon className="h-5 w-5" />
              <span className="hidden md:inline">Neue Planung</span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile-Titel (kein hidden-Header) */}
      <div className="finance-mobile-heading flex items-center gap-3 px-1 pt-4 pb-2 sm:hidden">
        <span className="finance-header-icon !h-10 !w-10 !basis-10"><CalculatorIcon /></span>
        <div>
          <p className="finance-kicker">Finanzen · Vorausschau</p>
          <h1 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">Budget-Planung</h1>
        </div>
      </div>

      {/* Mobile FAB: „Neue Planung" bleibt auf Phones erreichbar */}
      <button
        onClick={() => setShowCreateModal(true)}
        aria-label="Neue Planung"
        className="sm:hidden fixed bottom-5 right-5 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-primary-600 text-white shadow-lg hover:bg-primary-700 active:bg-primary-800"
      >
        <PlusIcon className="h-6 w-6" />
      </button>

      <div className="px-1 sm:px-0">
        {/* Legende Soll/Ist */}
        {!loading && budgetPlans.length > 0 && (
          <div className="mb-4 flex items-start gap-2 rounded-2xl border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-[#1c2432]">
            <InformationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-info-500" />
            <p className="text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
              <span className="font-semibold text-gray-800 dark:text-gray-200">Soll</span> = geplanter Jahresbetrag dieser Planung ·{' '}
              <span className="font-semibold text-gray-800 dark:text-gray-200">Ist</span> = tatsächliche Buchungen. Bei laufenden Jahren wird
              zusätzlich der anteilige Erwartungswert zum heutigen Stand gezeigt.
            </p>
          </div>
        )}

        {/* Tabs je Planung + separater Gesamt-Toggle */}
        {!loading && budgetPlans.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              {sortedTabs.map(plan => {
                const status = getPlanStatus(getPlanMetrics(plan));
                const dotColor = status.level === 'green' ? 'bg-success-500' : status.level === 'orange' ? 'bg-warning-500' : 'bg-error-500';
                const isActive = viewMode === 'plan' && markedPlanId === plan.id;
                return (
                  <button
                    key={plan.id}
                    onClick={() => switchPlan(plan.id)}
                    className={`inline-flex min-h-[40px] items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                      isActive
                        ? 'border-info-500 bg-info-50 text-info-700 dark:border-info-400 dark:bg-info-900/30 dark:text-info-300'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-info-300 dark:border-gray-700 dark:bg-[#1c2432] dark:text-gray-300 dark:hover:border-info-500'
                    }`}
                  >
                    <span className={`h-2 w-2 rounded-full flex-shrink-0 ${dotColor}`} />
                    {plan.name}
                  </button>
                );
              })}
            </div>
            <button
              onClick={toggleAggregate}
              className={`inline-flex min-h-[40px] items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                viewMode === 'aggregate'
                  ? 'border-info-500 bg-info-50 text-info-700 dark:border-info-400 dark:bg-info-900/30 dark:text-info-300'
                  : 'border-transparent bg-transparent text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
              }`}
            >
              <ChartBarIcon className="h-4 w-4" />
              Gesamt (alle Planungen)
            </button>
          </div>
        )}

        {loading ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center dark:border-gray-700 dark:bg-[#1c2432]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-gray-600 dark:text-gray-400 mt-2">Lade Budget-Planungen...</p>
          </div>
        ) : budgetPlans.length === 0 ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-6 text-center dark:border-gray-700 dark:bg-[#1c2432]">
            <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Keine Budget-Planungen
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Erstellen Sie Ihre erste Budget-Planung für das kommende Jahr.
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <PlusIcon className="h-5 w-5" />
              Erste Planung erstellen
            </button>
          </div>
        ) : viewMode === 'aggregate' ? (
          /* ---------------- Aggregat-Ansicht (nur Soll-Summen, kein Bearbeiten) ---------------- */
          <div className="space-y-4 mb-6">
            <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-[#1c2432] sm:p-5">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                Kombinierte Kennzahlen – {budgetPlans.length} {budgetPlans.length === 1 ? 'Planung' : 'Planungen'}
              </h3>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
                Nur zur Übersicht. Bearbeiten ist ausschließlich je Einzelplanung möglich – oben eine Planung auswählen.
              </p>
            </div>
            <StatGroup columns={3}>
              <StatCard
                icon={CurrencyEuroIcon}
                tone={aggregateStatistics.netBudget >= 0 ? 'success' : 'error'}
                valueTone={aggregateStatistics.netBudget >= 0 ? 'success' : 'error'}
                label="Netto (geplant)"
                value={formatCurrency(aggregateStatistics.netBudget)}
              />
              <StatCard
                icon={ArrowTrendingUpIcon}
                tone="success"
                valueTone="success"
                label="Einnahmen (geplant)"
                value={formatCurrency(aggregateStatistics.totalIncome)}
              />
              <StatCard
                icon={ArrowTrendingDownIcon}
                tone="error"
                valueTone="error"
                label="Ausgaben (geplant)"
                value={formatCurrency(aggregateStatistics.totalExpense)}
              />
            </StatGroup>
          </div>
        ) : activePlan && activeMetrics && activeStatus ? (
          /* ---------------- Aktive Planung: Kopf, KPIs, Status, Kontoverlauf, Kategorien ---------------- */
          <div className="mb-6">

            {/* Kopfzeile-Karte */}
            <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-[#1c2432] sm:p-5 mb-4">
              {!isEditingPlan ? (
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-lg font-bold text-gray-900 dark:text-white sm:text-xl">{activePlan.name}</h2>
                      <span className="inline-flex items-center gap-1 rounded-full bg-info-100 px-2.5 py-1 text-xs font-semibold text-info-700 dark:bg-info-900/30 dark:text-info-300">
                        <CalendarIcon className="h-3.5 w-3.5" /> {activePlan.year}
                      </span>
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                        activeStatus.level === 'green'
                          ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-300'
                          : activeStatus.level === 'orange'
                            ? 'bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300'
                            : 'bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-300'
                      }`}>
                        {activeStatus.level === 'green' ? <CheckCircleIcon className="h-3.5 w-3.5" /> : <ExclamationTriangleIcon className="h-3.5 w-3.5" />}
                        {activeStatus.label}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {activeMetrics.progressPct >= 99
                        ? 'Jahr abgeschlossen'
                        : `Stand heute · ${Math.round(activeMetrics.progressPct)}% des Jahres`}
                    </p>
                  </div>
                  <div className="flex flex-shrink-0 gap-2">
                    <button
                      onClick={() => enterEditMode(activePlan)}
                      className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg border border-gray-200 px-3.5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700/50"
                    >
                      <PencilSquareIcon className="h-4 w-4" /> Bearbeiten
                    </button>
                    <button
                      onClick={() => guardUnsavedChanges(() => handleDeleteBudgetPlan(activePlan))}
                      className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg border border-error-200 px-3.5 py-2 text-sm font-semibold text-error-600 hover:bg-error-50 dark:border-error-800 dark:text-error-400 dark:hover:bg-error-900/20"
                    >
                      <TrashIcon className="h-4 w-4" /> Löschen
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-[240px] flex-1">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Planung bearbeiten</span>
                    <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          Name der Planung
                        </label>
                        <input
                          type="text"
                          value={draftName}
                          onChange={(e) => { setDraftName(e.target.value); setIsDirty(true); }}
                          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          Planungsjahr
                        </label>
                        <input
                          type="number"
                          value={draftYear}
                          onChange={(e) => { setDraftYear(parseInt(e.target.value) || draftYear); setIsDirty(true); }}
                          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-shrink-0 gap-2">
                    <button
                      onClick={handleCancelEdit}
                      className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg border border-gray-200 px-3.5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700/50"
                    >
                      <XMarkIcon className="h-4 w-4" /> Abbrechen
                    </button>
                    <button
                      onClick={() => handleSaveEdit(activePlan)}
                      disabled={loading}
                      className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg bg-primary-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
                    >
                      <CheckIcon className="h-4 w-4" /> Speichern
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* KPI-Zeile Plan vs Ist + Status-Banner */}
            <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-[#1c2432] sm:p-5 mb-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {showIncomeTop && (
                  <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                    <div className="mb-3 flex items-center gap-2">
                      <div className="rounded-lg bg-success-500 p-1.5"><ArrowTrendingUpIcon className="h-4 w-4 text-white" /></div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Einnahmen</span>
                    </div>
                    <p className="text-2xl font-bold leading-tight tabular-nums text-success-600 dark:text-success-400">
                      {formatCurrency(activeMetrics.istIncome)}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">von {formatCurrency(activeMetrics.sollIncome)} geplant</p>
                    <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                      <div
                        className="h-full rounded-full bg-success-500 transition-all duration-500"
                        style={{ width: `${barPct(activeMetrics.istIncome, activeMetrics.sollIncome)}%` }}
                      />
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-xs text-gray-400 dark:text-gray-500">Abweichung</span>
                      <span className={`text-xs font-semibold ${activeMetrics.istIncome >= activeMetrics.sollIncome ? 'text-success-600 dark:text-success-400' : 'text-warning-600 dark:text-warning-400'}`}>
                        {activeMetrics.istIncome - activeMetrics.sollIncome >= 0 ? '+' : ''}{formatCurrency(activeMetrics.istIncome - activeMetrics.sollIncome)}
                      </span>
                    </div>
                    {activeMetrics.progressPct < 99 && (
                      <p className="mt-2 text-[11px] text-gray-400 dark:text-gray-500">
                        Erwartet bis heute ({Math.round(activeMetrics.progressPct)}% des Jahres): {formatCurrency(activeMetrics.sollIncome * (activeMetrics.progressPct / 100))}
                      </p>
                    )}
                  </div>
                )}

                {showExpenseTop && (
                  <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                    <div className="mb-3 flex items-center gap-2">
                      <div className="rounded-lg bg-error-500 p-1.5"><ArrowTrendingDownIcon className="h-4 w-4 text-white" /></div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Ausgaben</span>
                    </div>
                    <p className="text-2xl font-bold leading-tight tabular-nums text-error-600 dark:text-error-400">
                      {formatCurrency(activeMetrics.istExpense)}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">von {formatCurrency(activeMetrics.sollExpense)} geplant</p>
                    <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                      <div
                        className="h-full rounded-full bg-error-500 transition-all duration-500"
                        style={{ width: `${barPct(activeMetrics.istExpense, activeMetrics.sollExpense)}%` }}
                      />
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-xs text-gray-400 dark:text-gray-500">Abweichung</span>
                      <span className={`text-xs font-semibold ${activeMetrics.istExpense <= activeMetrics.sollExpense ? 'text-success-600 dark:text-success-400' : 'text-warning-600 dark:text-warning-400'}`}>
                        {activeMetrics.istExpense - activeMetrics.sollExpense >= 0 ? '+' : ''}{formatCurrency(activeMetrics.istExpense - activeMetrics.sollExpense)}
                      </span>
                    </div>
                    {activeMetrics.progressPct < 99 && (
                      <p className="mt-2 text-[11px] text-gray-400 dark:text-gray-500">
                        Erwartet bis heute ({Math.round(activeMetrics.progressPct)}% des Jahres): {formatCurrency(activeMetrics.sollExpense * (activeMetrics.progressPct / 100))}
                      </p>
                    )}
                  </div>
                )}

                <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                  <div className="mb-3 flex items-center gap-2">
                    <div className="rounded-lg bg-info-500 p-1.5"><CurrencyEuroIcon className="h-4 w-4 text-white" /></div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Netto</span>
                  </div>
                  <p className={`text-2xl font-bold leading-tight tabular-nums ${activeMetrics.istNet >= 0 ? 'text-info-600 dark:text-info-400' : 'text-warning-600 dark:text-warning-400'}`}>
                    {activeMetrics.istNet >= 0 ? '+' : ''}{formatCurrency(activeMetrics.istNet)}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Plan {activeMetrics.sollNet >= 0 ? '+' : ''}{formatCurrency(activeMetrics.sollNet)}</p>
                  <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3 dark:border-gray-700">
                    <span className="text-xs text-gray-400 dark:text-gray-500">Abweichung</span>
                    <span className={`text-xs font-semibold ${activeMetrics.istNet - activeMetrics.sollNet >= 0 ? 'text-success-600 dark:text-success-400' : 'text-warning-600 dark:text-warning-400'}`}>
                      {activeMetrics.istNet - activeMetrics.sollNet >= 0 ? '+' : ''}{formatCurrency(activeMetrics.istNet - activeMetrics.sollNet)}
                    </span>
                  </div>
                  {activeMetrics.progressPct < 99 && (
                    <p className="mt-2 text-[11px] text-gray-400 dark:text-gray-500">
                      Erwartet bis heute: {formatCurrency(activeMetrics.sollNet * (activeMetrics.progressPct / 100))}
                    </p>
                  )}
                </div>
              </div>

              {/* Status-Banner */}
              <div className={`mt-3 flex items-start gap-2.5 rounded-xl border-l-4 p-3 ${
                activeStatus.level === 'green'
                  ? 'border-success-500 bg-success-50 dark:bg-success-900/20'
                  : activeStatus.level === 'orange'
                    ? 'border-warning-500 bg-warning-50 dark:bg-warning-900/20'
                    : 'border-error-500 bg-error-50 dark:bg-error-900/20'
              }`}>
                {activeStatus.level === 'green'
                  ? <CheckCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5 text-success-600 dark:text-success-400" />
                  : <ExclamationTriangleIcon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${activeStatus.level === 'red' ? 'text-error-600 dark:text-error-400' : 'text-warning-600 dark:text-warning-400'}`} />}
                <p className="text-sm text-gray-800 dark:text-gray-200">
                  {renderStatusMessage(activeMetrics, activeStatus)}
                </p>
              </div>
            </div>

            {/* Kontoverlauf – standardmäßig eingeklappt */}
            <details
              className="mb-4 rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-[#1c2432] group"
              open={chartAccordionOpen}
              onToggle={(e) => setChartAccordionOpen((e.target as HTMLDetailsElement).open)}
            >
              <summary className="flex min-h-[40px] cursor-pointer list-none items-center justify-between gap-2 p-4 text-sm font-semibold text-gray-900 dark:text-white sm:p-5">
                <span className="flex items-center gap-2">
                  <ChartBarIcon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                  Kontoverlauf dieser Planung
                </span>
                <ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-gray-400 transition-transform group-open:rotate-180" />
              </summary>
              <div className="px-4 pb-4 sm:px-5 sm:pb-5">
                {chartDataLoading ? (
                  <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">Lade Chart-Daten…</div>
                ) : combinedChartData.length > 0 ? (
                  <div style={{ height: '280px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={combinedChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="monat"
                          tickFormatter={(value) => {
                            if (typeof value === 'string' && value.includes('-')) {
                              const [year, month] = value.split('-');
                              return `${month}/${year.slice(-2)}`;
                            }
                            return value;
                          }}
                        />
                        <YAxis
                          tickFormatter={(value) => {
                            const absValue = Math.abs(value);
                            if (absValue >= 1000) {
                              return `${(value / 1000).toFixed(1)}k`;
                            }
                            return value.toFixed(0);
                          }}
                          tick={{ fontSize: 11 }}
                          width={50}
                        />
                        <RechartsTooltip formatter={(value: number) => formatCurrency(value)} />
                        <Line
                          type="monotone"
                          dataKey="Kontosaldo"
                          stroke="#3b82f6"
                          strokeWidth={3}
                          dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                          name="Gesamtkontosaldo"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">Keine Kontodaten für diesen Zeitraum.</div>
                )}
              </div>
            </details>

            {/* Kategorien */}
            <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-[#1c2432] sm:p-5">
              <h3 className="mb-1 text-base font-semibold text-gray-900 dark:text-white">
                Kategorien ({isEditingPlan ? Object.keys(draftBudget).length : activeMetrics.rows.length})
              </h3>
              <p className="mb-4 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
                {isEditingPlan
                  ? 'Bearbeitungsmodus: Soll-Beträge ändern, Kategorien hinzufügen oder entfernen. Ist-Werte bleiben unverändert (historische Buchungen).'
                  : 'Klick auf eine Kategorie zeigt die zugehörigen Buchungen. Soll-Werte stammen aus dieser Planung, Ist-Werte aus den tatsächlichen Kontobuchungen.'}
              </p>

              {!isEditingPlan ? (
                <div className="space-y-2">
                  {activeMetrics.rows.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-gray-300 py-8 text-center text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400">
                      Noch keine Kategorien mit Werten in dieser Planung.
                    </div>
                  ) : (
                    activeMetrics.rows.map((row) => {
                      const IconComponent = iconMap[row.icon] || iconMap.default;
                      const catNet = row.istIncome - row.istExpense;
                      const sollCatNet = row.sollIncome - row.sollExpense;
                      const showInc = row.sollIncome > 0 || row.istIncome > 0;
                      const showExp = row.sollExpense > 0 || row.istExpense > 0;
                      const isExpanded = expandedCategory === row.name;
                      const incRatio = barPct(row.istIncome, row.sollIncome);
                      const expRatioRaw = row.sollExpense > 0 ? (row.istExpense / row.sollExpense) * 100 : (row.istExpense > 0 ? 100 : 0);
                      const expRatio = Math.min(expRatioRaw, 100);

                      return (
                        <div key={row.name} className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                          <button
                            onClick={() => setExpandedCategory(isExpanded ? null : row.name)}
                            className="min-h-[40px] w-full p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40 sm:p-4"
                          >
                            <div className="flex items-center gap-3">
                              <div className="flex-shrink-0 rounded-lg bg-primary-100 p-1.5 dark:bg-primary-900/30">
                                <IconComponent className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                              </div>
                              <span className="flex-1 truncate font-semibold text-gray-900 dark:text-white">{row.name}</span>
                              <span className={`flex-shrink-0 text-sm font-bold tabular-nums ${catNet >= 0 ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400'}`}>
                                {catNet >= 0 ? '+' : ''}{formatCurrency(catNet)}
                              </span>
                              <ChevronDownIcon className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                            </div>

                            <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
                              {showInc && (
                                <div>
                                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Einnahmen</p>
                                  <p className="text-sm font-bold tabular-nums text-success-600 dark:text-success-400">{formatCurrency(row.istIncome)}</p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">von {formatCurrency(row.sollIncome)}</p>
                                  <div className="mt-1 h-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                                    <div className="h-full rounded-full bg-success-500" style={{ width: `${incRatio}%` }} />
                                  </div>
                                </div>
                              )}
                              {showExp && (
                                <div>
                                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Ausgaben</p>
                                  <p className="text-sm font-bold tabular-nums text-error-600 dark:text-error-400">{formatCurrency(row.istExpense)}</p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">von {formatCurrency(row.sollExpense)}</p>
                                  <div className="mt-1 h-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                                    <div className="h-full rounded-full bg-error-500" style={{ width: `${expRatio}%` }} />
                                  </div>
                                  {expRatioRaw > 100 && (
                                    <p className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-error-600 dark:text-error-400">
                                      <ExclamationTriangleIcon className="h-3 w-3" /> {Math.round(expRatioRaw)}% verbraucht
                                    </p>
                                  )}
                                </div>
                              )}
                              <div>
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Netto</p>
                                <p className={`text-sm font-bold tabular-nums ${catNet >= 0 ? 'text-info-600 dark:text-info-400' : 'text-warning-600 dark:text-warning-400'}`}>
                                  {catNet >= 0 ? '+' : ''}{formatCurrency(catNet)}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Plan {sollCatNet >= 0 ? '+' : ''}{formatCurrency(sollCatNet)}</p>
                              </div>
                            </div>
                          </button>

                          {isExpanded && (
                            <div className="border-t border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40 sm:p-4">
                              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                Buchungen ({row.bookings.length})
                              </p>
                              {row.bookings.length === 0 ? (
                                <p className="py-3 text-center text-sm text-gray-500 dark:text-gray-400">Keine Buchungen gefunden.</p>
                              ) : (
                                <div className="space-y-1.5">
                                  {row.bookings.map((booking: any) => {
                                    const sign = Number(booking.betrag) >= 0 ? 1 : -1;
                                    const amount = (booking.categories || [])
                                      .filter((cat: any) => categories.find(c => c.id === cat.kategorie_id)?.name === row.name)
                                      .reduce((a: number, cat: any) => a + Number(cat.betrag) * sign, 0);
                                    return (
                                      <div
                                        key={booking.id}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => setSelectedStatementId(String(booking.id))}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setSelectedStatementId(String(booking.id));
                                          }
                                        }}
                                        className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-2.5 transition-colors hover:border-primary-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/40 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-primary-700 dark:hover:bg-gray-700/50"
                                      >
                                        <div className="min-w-0">
                                          <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                                            {booking.verwendungszweck || booking.buchungstext || 'Keine Beschreibung'}
                                          </p>
                                          <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                                            {new Date(booking.buchungstag).toLocaleDateString('de-DE')}
                                            {booking.name_zahlungsbeteiligter ? ` · ${booking.name_zahlungsbeteiligter}` : ''}
                                          </p>
                                        </div>
                                        <span className={`flex-shrink-0 text-sm font-bold tabular-nums ${amount >= 0 ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400'}`}>
                                          {amount >= 0 ? '+' : ''}{formatCurrency(amount)}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {Object.keys(draftBudget).length === 0 ? (
                    <div className="rounded-xl border border-dashed border-gray-300 py-6 text-center text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400">
                      Noch keine Kategorien in dieser Planung.
                    </div>
                  ) : (
                    Object.entries(draftBudget).map(([name, data]) => {
                      const category = categories.find(c => c.name === name);
                      const IconComponent = category?.icon ? (iconMap[category.icon] || iconMap.default) : iconMap.default;
                      const income = typeof data.income === 'string' ? parseFloat(data.income) || 0 : (data.income || 0);
                      const expense = typeof data.expense === 'string' ? parseFloat(data.expense) || 0 : (data.expense || 0);
                      const net = income - expense;
                      const existingRow = activeMetrics.rows.find(r => r.name === name);
                      return (
                        <div key={name} className="grid grid-cols-1 gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/30 sm:grid-cols-4 sm:items-center sm:p-4">
                          <div className="flex items-center gap-2">
                            <div className="flex-shrink-0 rounded-lg bg-primary-100 p-1.5 dark:bg-primary-900/30">
                              <IconComponent className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                            </div>
                            <span className="truncate text-sm font-semibold text-gray-900 dark:text-white" title={name}>{name}</span>
                            <button
                              onClick={() => handleRemoveDraftCategory(name)}
                              className="ml-auto flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-error-500 hover:bg-error-50 dark:hover:bg-error-900/20"
                              title="Kategorie entfernen"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                          <div>
                            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                              Soll Einnahmen (€)
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="50"
                              value={data.income}
                              onChange={(e) => handleDraftCategoryChange(name, 'income', e.target.value)}
                              className="w-full rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                            />
                            {!!existingRow?.istIncome && (
                              <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">Ist bisher: {formatCurrency(existingRow.istIncome)}</p>
                            )}
                          </div>
                          <div>
                            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                              Soll Ausgaben (€)
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="50"
                              value={data.expense}
                              onChange={(e) => handleDraftCategoryChange(name, 'expense', e.target.value)}
                              className="w-full rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                            />
                            {!!existingRow?.istExpense && (
                              <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">Ist bisher: {formatCurrency(existingRow.istExpense)}</p>
                            )}
                          </div>
                          <div className="text-left sm:text-right">
                            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                              Netto (Soll)
                            </label>
                            <p className={`text-sm font-bold tabular-nums ${net >= 0 ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400'}`}>
                              {net >= 0 ? '+' : ''}{formatCurrency(net)}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}

                  <button
                    onClick={handleAddDraftCategory}
                    className="inline-flex min-h-[40px] items-center gap-2 rounded-lg border border-dashed border-gray-300 px-3.5 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-800"
                  >
                    <PlusIcon className="h-4 w-4" /> Kategorie hinzufügen
                  </button>

                  <div className="sticky bottom-3 flex flex-wrap items-center justify-end gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-md dark:border-gray-700 dark:bg-[#1c2432]">
                    <div className="mr-auto text-sm text-gray-500 dark:text-gray-400">
                      Neues Soll-Netto:{' '}
                      <span className={`font-bold ${draftSollNet >= 0 ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400'}`}>
                        {formatCurrency(draftSollNet)}
                      </span>
                    </div>
                    <button
                      onClick={handleCancelEdit}
                      className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg border border-gray-200 px-3.5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700/50"
                    >
                      Abbrechen
                    </button>
                    <button
                      onClick={() => handleSaveEdit(activePlan)}
                      disabled={loading}
                      className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg bg-primary-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
                    >
                      Speichern
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>

      {/* Multi-Step Modal zum Erstellen neuer Budget-Planung */}
      {showCreateModal && (
        <ModalShell
          open={showCreateModal}
          onClose={resetModal}
          variant="form"
          size="lg"
          icon={CalculatorIcon}
          title="Neue Budget-Planung"
          closeOnBackdrop={false}
          footer={
            <div className="flex w-full items-center justify-between gap-3">
              <button
                onClick={prevStep}
                disabled={currentStep === 1}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Zurück
              </button>

              <div className="flex gap-3">
                <button
                  onClick={resetModal}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Abbrechen
                </button>

                {currentStep < 3 ? (
                  <button
                    onClick={nextStep}
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Weiter
                  </button>
                ) : (
                  <button
                    onClick={handleCreateBudgetPlan}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Wird erstellt...
                      </>
                    ) : (
                      <>
                        <CheckIcon className="h-4 w-4" />
                        Budget-Planung erstellen
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          }
        >
          {/* Schritt-Indikator: drei gleich breite Stationen, damit auf Phones
              keine Stufe ausserhalb des sichtbaren Bereichs liegt. */}
          <ol className="mb-6 grid grid-cols-3 gap-2" aria-label="Fortschritt der Budget-Planung">
            {[1, 2, 3].map((step) => {
              const active = step === currentStep;
              const complete = step < currentStep;
              return (
                <li
                  key={step}
                  aria-current={active ? 'step' : undefined}
                  className={`finance-budget-step min-w-0 rounded-xl border px-2 py-2.5 transition-colors sm:px-3 ${
                    active
                      ? 'border-primary-300 bg-primary-50 dark:border-primary-700 dark:bg-primary-900/25'
                      : complete
                        ? 'border-success-200 bg-success-50 dark:border-success-800 dark:bg-success-900/20'
                        : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/60'
                  }`}
                >
                  <div className="finance-budget-step-row flex min-w-0 items-center gap-2">
                    <span className={`finance-budget-step-index flex h-7 w-7 flex-none items-center justify-center rounded-full border text-xs font-bold ${
                      active
                        ? 'border-primary-600 bg-primary-600 text-white'
                        : complete
                          ? 'border-success-600 bg-success-600 text-white'
                          : 'border-gray-300 text-gray-500 dark:border-gray-600 dark:text-gray-400'
                    }`}>
                      {complete ? <CheckIcon className="h-3.5 w-3.5" /> : step}
                    </span>
                    <span className={`min-w-0 truncate text-xs font-bold sm:text-sm ${
                      active
                        ? 'text-primary-700 dark:text-primary-300'
                        : complete
                          ? 'text-success-700 dark:text-success-300'
                          : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {step === 1 && 'Daten'}
                      {step === 2 && 'Budget'}
                      {step === 3 && 'Prüfen'}
                    </span>
                  </div>
                  <p className="mt-1 hidden pl-9 text-[11px] leading-4 text-gray-500 dark:text-gray-400 sm:block">
                    {step === 1 && 'Name & Quelle'}
                    {step === 2 && 'Kategorien'}
                    {step === 3 && 'Abschluss'}
                  </p>
                </li>
              );
            })}
          </ol>

          {/* Schritt 1: Grunddaten */}
          {currentStep === 1 && (
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                      Grundeinstellungen für die Budget-Planung
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div>
                        <AttributeLabel iconKey="name" className="block mb-2" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Name der Planung *
                        </AttributeLabel>
                        <input
                          type="text"
                          value={stepData.step1.name}
                          onChange={(e) => setStepData(prev => ({
                            ...prev,
                            step1: { ...prev.step1, name: e.target.value }
                          }))}
                          className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          placeholder="z.B. Budget 2025"
                        />
                      </div>

                      <div>
                        <AttributeLabel iconKey="year" className="block mb-2" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Planungsjahr
                        </AttributeLabel>
                        <input
                          type="number"
                          value={stepData.step1.year}
                          onChange={(e) => setStepData(prev => ({
                            ...prev,
                            step1: { ...prev.step1, year: parseInt(e.target.value) }
                          }))}
                          className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          min="2020"
                          max="2030"
                        />
                      </div>
                    </div>

                    {/* Import-Optionen */}
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                      <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                        Ausgangsdaten für die Planung
                      </h5>
                      <div className="space-y-3">
                        <label className="flex items-start">
                          <input
                            type="radio"
                            name="importMode"
                            checked={stepData.step1.useImport}
                            onChange={() => setStepData(prev => ({
                              ...prev,
                              step1: { ...prev.step1, useImport: true }
                            }))}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 mt-0.5"
                          />
                          <div className="ml-3">
                            <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                              Daten aus vorhandenem Jahr importieren
                            </span>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Übernimmt vorhandene Kategorie-Beträge als Ausgangsbasis
                            </p>
                          </div>
                        </label>

                        {stepData.step1.useImport && (
                          <div className="ml-7">
                            <AttributeLabel iconKey="year" size="sm" className="block mb-1" textClassName="text-xs font-medium text-gray-600 dark:text-gray-400">
                              Jahr für Import:
                            </AttributeLabel>
                            <input
                              type="number"
                              value={stepData.step1.importYear}
                              onChange={(e) => setStepData(prev => ({
                                ...prev,
                                step1: { ...prev.step1, importYear: parseInt(e.target.value) }
                              }))}
                              className="w-32 p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                              min="2020"
                              max="2025"
                            />
                          </div>
                        )}

                        <label className="flex items-start">
                          <input
                            type="radio"
                            name="importMode"
                            checked={!stepData.step1.useImport}
                            onChange={() => setStepData(prev => ({
                              ...prev,
                              step1: { ...prev.step1, useImport: false }
                            }))}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 mt-0.5"
                          />
                          <div className="ml-3">
                            <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                              Komplett neue Planung
                            </span>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Beginnt mit leeren Kategorien für komplette Neueingabe
                            </p>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Schritt 2: Budget bearbeiten */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                          Budget-Kategorien bearbeiten
                        </h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Passen Sie die Einnahmen und Ausgaben für jede Kategorie an
                        </p>
                      </div>
                      <button
                        onClick={addStep2Category}
                        className="flex items-center gap-2 px-3 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                      >
                        <PlusIcon className="h-4 w-4" />
                        Kategorie hinzufügen
                      </button>
                    </div>

                    {/* Summen-Übersicht */}
                    <div className="mb-4">
                      <StatGroup columns={3}>
                        <StatCard
                          icon={ArrowTrendingUpIcon}
                          tone="success"
                          valueTone="success"
                          label="Geplante Einnahmen"
                          value={new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(
                            Object.values(stepData.step2.budgetData).reduce((sum, cat: any) => sum + (parseFloat(cat.income) || 0), 0)
                          )}
                        />
                        <StatCard
                          icon={ArrowTrendingDownIcon}
                          tone="error"
                          valueTone="error"
                          label="Geplante Ausgaben"
                          value={new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(
                            Object.values(stepData.step2.budgetData).reduce((sum, cat: any) => sum + (parseFloat(cat.expense) || 0), 0)
                          )}
                        />
                        <StatCard
                          icon={CurrencyEuroIcon}
                          tone={
                            Object.values(stepData.step2.budgetData).reduce((sum, cat: any) => sum + (parseFloat(cat.income) || 0) - (parseFloat(cat.expense) || 0), 0) >= 0
                              ? 'info'
                              : 'warning'
                          }
                          valueTone={
                            Object.values(stepData.step2.budgetData).reduce((sum, cat: any) => sum + (parseFloat(cat.income) || 0) - (parseFloat(cat.expense) || 0), 0) >= 0
                              ? 'info'
                              : 'warning'
                          }
                          label="Geplante Bilanz"
                          value={
                            <>
                              {Object.values(stepData.step2.budgetData).reduce((sum, cat: any) => sum + (parseFloat(cat.income) || 0) - (parseFloat(cat.expense) || 0), 0) >= 0 ? '+' : ''}
                              {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(
                                Object.values(stepData.step2.budgetData).reduce((sum, cat: any) => sum + (parseFloat(cat.income) || 0) - (parseFloat(cat.expense) || 0), 0)
                              )}
                            </>
                          }
                        />
                      </StatGroup>
                    </div>

                    <div className="space-y-3 max-h-[500px] sm:max-h-[600px] overflow-y-auto">
                      {Object.entries(stepData.step2.budgetData).map(([categoryName, data]) => {
                        // Find category icon
                        const category = categories.find(cat => cat.name === categoryName);
                        const IconComponent = category?.icon ? (iconMap[category.icon] || iconMap.default) : iconMap.default;

                        return (
                        <div key={categoryName} className="grid grid-cols-1 sm:grid-cols-4 gap-3 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg">
                          <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex-shrink-0">
                              <IconComponent className="h-3.5 w-3.5 text-primary-600 dark:text-primary-400" />
                            </div>
                            <span className="text-sm font-medium text-gray-900 dark:text-white truncate" title={categoryName}>
                              {categoryName}
                            </span>
                            <button
                              onClick={() => removeStep2Category(categoryName)}
                              className="p-1 text-error-500 hover:text-error-700 transition-colors"
                              title="Kategorie entfernen"
                            >
                              <XMarkIcon className="h-3 w-3" />
                            </button>
                          </div>

                          <div>
                            <AttributeLabel iconKey="income" size="sm" className="block mb-1" textClassName="text-xs text-gray-500 dark:text-gray-400 font-medium">
                              Einnahmen (€)
                            </AttributeLabel>
                            <input
                              type="number"
                              step="0.01"
                              value={data.income}
                              onChange={(e) => handleStep2BudgetChange(categoryName, 'income', e.target.value)}
                              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                              placeholder="0.00"
                            />
                          </div>

                          <div>
                            <AttributeLabel iconKey="expense" size="sm" className="block mb-1" textClassName="text-xs text-gray-500 dark:text-gray-400 font-medium">
                              Ausgaben (€)
                            </AttributeLabel>
                            <input
                              type="number"
                              step="0.01"
                              value={data.expense}
                              onChange={(e) => handleStep2BudgetChange(categoryName, 'expense', e.target.value)}
                              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                              placeholder="0.00"
                            />
                          </div>

                          <div>
                            <AttributeLabel iconKey="balance" size="sm" className="block mb-1" textClassName="text-xs text-gray-500 dark:text-gray-400 font-medium">
                              Netto
                            </AttributeLabel>
                            <div className={`p-2 text-sm font-medium rounded ${
                              (parseFloat(data.income as string || '0') - parseFloat(data.expense as string || '0')) >= 0
                                ? 'text-success-600 dark:text-success-400 bg-success-50 dark:bg-success-900/20'
                                : 'text-error-600 dark:text-error-400 bg-error-50 dark:bg-error-900/20'
                            }`}>
                              {formatCurrency(parseFloat(data.income as string || '0') - parseFloat(data.expense as string || '0'))}
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>

                    {Object.keys(stepData.step2.budgetData).length === 0 && (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <TagIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-sm font-medium">Noch keine Kategorien vorhanden</p>
                        <p className="text-xs">Klicken Sie auf "Kategorie hinzufügen" um zu beginnen</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Schritt 3: Zusammenfassung */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                      Zusammenfassung der Budget-Planung
                    </h4>

                    {/* Übersicht */}
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <AttributeLabel iconKey="name" size="sm" textClassName="text-xs text-gray-500 dark:text-gray-400 font-medium">PLANUNGSNAME</AttributeLabel>
                          <p className="text-sm font-bold text-gray-900 dark:text-white">{stepData.step1.name}</p>
                        </div>
                        <div>
                          <AttributeLabel iconKey="year" size="sm" textClassName="text-xs text-gray-500 dark:text-gray-400 font-medium">PLANUNGSJAHR</AttributeLabel>
                          <p className="text-sm font-bold text-gray-900 dark:text-white">{stepData.step1.year}</p>
                        </div>
                        <div>
                          <AttributeLabel iconKey="category" size="sm" textClassName="text-xs text-gray-500 dark:text-gray-400 font-medium">KATEGORIEN</AttributeLabel>
                          <p className="text-sm font-bold text-gray-900 dark:text-white">
                            {Object.keys(stepData.step2.budgetData).length} Kategorien
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Budget-Zusammenfassung */}
                    <div className="mb-6">
                      {(() => {
                        const totalIncome = Object.values(stepData.step2.budgetData).reduce((sum, data) => {
                          return sum + (parseFloat(data.income as string || '0') || 0);
                        }, 0);

                        const totalExpense = Object.values(stepData.step2.budgetData).reduce((sum, data) => {
                          return sum + (parseFloat(data.expense as string || '0') || 0);
                        }, 0);

                        const netBudget = totalIncome - totalExpense;

                        return (
                          <StatGroup columns={3}>
                            <StatCard
                              icon={ArrowTrendingUpIcon}
                              tone="success"
                              valueTone="success"
                              label="Gesamte Einnahmen"
                              value={formatCurrency(totalIncome)}
                            />
                            <StatCard
                              icon={ArrowTrendingDownIcon}
                              tone="error"
                              valueTone="error"
                              label="Gesamte Ausgaben"
                              value={formatCurrency(totalExpense)}
                            />
                            <StatCard
                              icon={ChartBarIcon}
                              tone={netBudget >= 0 ? 'info' : 'warning'}
                              valueTone={netBudget >= 0 ? 'info' : 'warning'}
                              label="Netto-Budget"
                              value={formatCurrency(netBudget)}
                              sub={netBudget >= 0 ? 'Erwarteter Überschuss' : 'Erwartetes Defizit'}
                            />
                          </StatGroup>
                        );
                      })()}
                    </div>

                    {/* Kategorie-Liste */}
                    <div>
                      <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                        Kategorien im Detail:
                      </h5>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {Object.entries(stepData.step2.budgetData).map(([categoryName, data]) => {
                          const income = parseFloat(data.income as string || '0') || 0;
                          const expense = parseFloat(data.expense as string || '0') || 0;
                          const net = income - expense;

                          return (
                            <div key={categoryName} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg">
                              <div className="flex items-center gap-2">
                                <TagIcon className="h-4 w-4 text-gray-400" />
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                  {categoryName}
                                </span>
                              </div>
                              <div className="flex items-center gap-4 text-sm">
                                <span className="text-success-600 dark:text-success-400">
                                  +{formatCurrency(income)}
                                </span>
                                <span className="text-error-600 dark:text-error-400">
                                  -{formatCurrency(expense)}
                                </span>
                                <span className={`font-medium ${
                                  net >= 0
                                    ? 'text-success-600 dark:text-success-400'
                                    : 'text-error-600 dark:text-error-400'
                                }`}>
                                  {formatCurrency(net)}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}
        </ModalShell>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        visible={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        variant={confirmModal.variant}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        button={confirmModal.variant === 'single' ? {
          text: 'OK',
          onPress: () => setConfirmModal({ ...confirmModal, isOpen: false })
        } : undefined}
        buttons={confirmModal.variant === 'two' ? {
          cancel: {
            text: 'Abbrechen',
            onPress: () => setConfirmModal({ ...confirmModal, isOpen: false })
          },
          confirm: {
            text: 'Bestätigen',
            onPress: () => {
              if (confirmModal.onConfirm) {
                confirmModal.onConfirm();
              }
              setConfirmModal({ ...confirmModal, isOpen: false });
            }
          }
        } : undefined}
      />

      {/* Category Input Modal */}
      {categoryInputModal.isOpen && (
        <ModalShell
          open={categoryInputModal.isOpen}
          onClose={() => {
            setCategoryInputModal({ isOpen: false, title: '', onConfirm: () => {} });
            setCategoryInputValue('');
          }}
          variant="form"
          size="sm"
          title={categoryInputModal.title}
          closeOnBackdrop={false}
          footer={
            <ModalActions
              secondary={{
                label: 'Abbrechen',
                onClick: () => {
                  setCategoryInputModal({ isOpen: false, title: '', onConfirm: () => {} });
                  setCategoryInputValue('');
                }
              }}
              primary={{
                label: 'Hinzufügen',
                onClick: () => {
                  if (categoryInputValue.trim()) {
                    categoryInputModal.onConfirm(categoryInputValue);
                  }
                },
                disabled: !categoryInputValue.trim()
              }}
            />
          }
        >
          <AttributeLabel iconKey="category" className="block mb-2" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
            Kategorie-Name:
          </AttributeLabel>
          <input
            type="text"
            value={categoryInputValue}
            onChange={(e) => setCategoryInputValue(e.target.value)}
            placeholder="Name der neuen Kategorie"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            onKeyPress={(e) => {
              if (e.key === 'Enter' && categoryInputValue.trim()) {
                categoryInputModal.onConfirm(categoryInputValue);
              }
            }}
            autoFocus
          />
        </ModalShell>
      )}

      {/* Buchungsdetail (Kategorien zuordnen etc.) */}
      {selectedStatementId && (
        <StatementDetailModal
          statementId={selectedStatementId}
          onClose={() => setSelectedStatementId(null)}
          onStatementUpdate={() => loadAccountStatements()}
          onStatementDelete={() => loadAccountStatements()}
        />
      )}
    </div>
  );
};

export default BudgetPlanning;
